import { useGetMarkValidationDetail, useRunValidations } from "@features/mx/meterReads/hooks";
import { toast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Progress } from "@shared/ui/progress";
import { useState } from "react";
import { useParams } from "react-router-dom";

interface RunValidationsDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  scheduleId: number;
  newReadingsPending?: number;
}

export function RunValidationsDialog({
  open,
  setOpen,
  scheduleId,
  newReadingsPending = 0,
}: RunValidationsDialogProps) {
  const { id } = useParams<{ id: string }>();
  const remoteUtilityId = useRemoteUtilityId();
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState(0);
  const [exemptedAfterValidation, setExemptedAfterValidation] = useState<number | null>(null);
  const { data: completionData, isLoading } = useGetMarkValidationDetail(remoteUtilityId, id);
  const {mutate: runPendingValidations} = useRunValidations();
 const handleValidate = async () => {
  if (isValidating) return;

  setIsValidating(true);
  setValidationProgress(0);

  const steps = [10, 28, 45, 63, 79, 90, 100];
  let i = 0;

  const timer = setInterval(() => {
    setValidationProgress((prev) => {
      if (i < steps.length) {
        return steps[i++];
      } else {
        clearInterval(timer);
        return prev;
      }
    });
  }, 400);

  try {
    await runPendingValidations({
      payload: {
        meterRunId: id,
      },
    }, {
      onSuccess: () =>{
        setOpen(false);
        toast({
          title: "Validation Started for the readings.",
          description: "Validations are running in background for this cycle's new readings. Please check the Validations list after some time. "
        })
      }
    });

    // Only runs on success
    setExemptedAfterValidation(0);
  } catch (error) {
    console.error("Validation failed:", error);
  } finally {
    // Always reset validation state
    clearInterval(timer);
    setIsValidating(false);
    setValidationProgress(100);
  }
};


  const handleClose = (forceClose = false) => {
    if (isValidating && !forceClose) return;
    
    setIsValidating(false);
    setValidationProgress(0);
    setExemptedAfterValidation(null);
    setOpen(false);
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(o) => {
        if (!isValidating) {
          handleClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Run Validations</DialogTitle>
          <DialogDescription>
            Validate newly submitted readings to detect anomalies and mark exempted readings. Pass and Revisit statuses are not changed by this validation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <div className="rounded-md border p-3">
            <p className="text-sm">
              New readings pending validation: <span className="font-medium">{completionData?.newReadingsPendingValidation}</span>
            </p>
            {exemptedAfterValidation !== null && (
              <p className="text-sm mt-1">
                Exempted after validation: <span className="font-medium">{exemptedAfterValidation}</span>
              </p>
            )}
          </div>

          {/* Explanation */}
          <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
            What this does:
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>Applies validation checks (e.g., negative/abnormal consumption, RCNT handling, photo presence).</li>
              <li>Only updates a reading's status to "exempted" when rules apply; "pass" and "revisit" are not modified here.</li>
              <li>You can verify updated readings and statuses in the Readings list.</li>
            </ul>
          </div>

          {/* Progress */}
          {isValidating && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Running validations…</span>
                <span>{validationProgress}%</span>
              </div>
              <Progress value={validationProgress} />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleClose()}
            disabled={isValidating}
          >
            {exemptedAfterValidation === null ? "Cancel" : "Close"}
          </Button>
          <Button
            onClick={handleValidate}
            disabled={isValidating} //  || newReadingsPending === 0
          >
            {isValidating ? "Validating…" : "Validate Readings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}